/**
 * Persistent bottom mini player — cover, title, play/pause, tap anywhere
 * else on the bar to open the full-screen /listen view. Sits above the
 * bottom tab bar; hidden when nothing is loaded, including after the close
 * button explicitly dismisses the current track.
 */
import { useEffect } from "react";
import { Pause, Play, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAudioPlayer } from "../lib/audioPlayer";
import { Cover } from "./Cover";

export function MiniPlayerBar() {
  const { track, isPlaying, toggle, close, currentTime, duration } = useAudioPlayer();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Tell the layout how much bottom chrome to reserve (index.css,
  // `--mini-player-h`): this bar is fixed, so a page that does not know it
  // is there ends with its last button underneath. Runs before the early
  // return below, which is why it is an effect and not inline JSX.
  useEffect(() => {
    document.body.dataset.miniPlayer = track ? "on" : "off";
    return () => {
      delete document.body.dataset.miniPlayer;
    };
  }, [track]);

  if (!track) return null;

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  return (
    <div
      className="safe-mini-player-offset focus-inset fixed inset-x-0 z-30 bg-surface border-t border-line flex items-center gap-3 px-3 py-2 cursor-pointer"
      onClick={() => navigate("/listen")}
      role="button"
      // `role="button"` sur un <div> n'apporte le clavier avec lui : sans
      // `tabIndex` la barre n'était pas atteignable, et sans gestionnaire de
      // touches elle ne s'activait ni par Entrée ni par Espace. Elle se
      // présentait donc comme un bouton aux lecteurs d'écran tout en
      // n'étant utilisable qu'à la souris. Un <button> serait plus simple,
      // mais la barre en contient déjà un (lecture/pause) et un bouton
      // imbriqué dans un bouton est du HTML invalide.
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault(); // Espace ferait défiler la page.
          navigate("/listen");
        }
      }}
      aria-label={t("player.nowPlaying")}
    >
      <div className="w-11 h-11 shrink-0">
        <Cover
          svg={track.coverSvg}
          imageUrl={track.coverImageUrl}
          imageThumbUrl={track.coverImageThumbUrl}
          title={track.title}
          colorway={track.coverColorway}
          colorKey={track.id}
          rounded="rounded-[6px]"
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-heading font-semibold text-sm text-paper truncate">{track.title}</p>
        <div className="h-1 mt-1 rounded-full bg-line overflow-hidden">
          <div className="h-full bg-sun" style={{ width: `${progress * 100}%` }} />
        </div>
      </div>
      <button
        type="button"
        className="tap-target shrink-0 flex items-center justify-center rounded-full bg-sun text-mint-ink overprint overprint-sm ink-coral"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        aria-label={isPlaying ? t("player.pause") : t("player.play")}
      >
        {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
      </button>
      <button
        type="button"
        className="tap-target shrink-0 flex items-center justify-center text-violet-soft"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
        aria-label={t("player.close")}
      >
        <X size={18} />
      </button>
    </div>
  );
}
